import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

interface AIQuota {
  allowed: boolean;
  used: number;
  limit: number;
}

export type AIAction =
  | { type: 'replace'; searchText: string; newText: string; applied?: boolean }
  | { type: 'insert'; newText: string; applied?: boolean }
  | { type: 'suggestion'; applied?: boolean }
  | {
      type: 'create_chapter';
      title: string;
      content?: string;
      position?: 'after_current' | 'before_current' | 'end' | 'start';
      sectionType?: string;
      applied?: boolean;
    }
  | { type: 'update_chapter'; chapterId: string; title?: string; applied?: boolean }
  | { type: 'delete_chapter'; chapterId: string; applied?: boolean }
  | { type: 'reorder_chapters'; order: string[]; applied?: boolean }
  | {
      type: 'move_content';
      fromChapterId: string;
      toChapterId: string;
      content: string;
      applied?: boolean;
    }
  // ── Manga/HQ ghostwriter actions — fill the page script, the Universe and
  // the premise, all reactive on the client. ────────────────────────────────
  | { type: 'manga_page_script'; script: string; applied?: boolean }
  | { type: 'create_character'; name: string; characterType: string; description: string; applied?: boolean }
  | { type: 'set_premise'; premise: string; applied?: boolean };

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  audioUrl?: string; // For voice messages
  timestamp: Date;
  // For actions performed by the AI (multiple actions supported)
  actions?: AIAction[];
}

interface AIState {
  // Quota
  quota: AIQuota | null;
  isLoadingQuota: boolean;
  fetchQuota: () => Promise<void>;

  // Processing state
  isProcessing: boolean;
  currentOperation: string | null;
  setProcessing: (operation: string | null) => void;

  // Chat state - now per project
  currentProjectId: string | null;
  messagesByProject: Record<string, ChatMessage[]>;
  messages: ChatMessage[]; // Derived from messagesByProject[currentProjectId]
  isChatOpen: boolean;
  isRecording: boolean;

  // Chat actions
  setCurrentProject: (projectId: string | null) => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  clearProjectMessages: (projectId: string) => void;
  setRecording: (recording: boolean) => void;
  applyAction: (messageId: string, actionIndex: number) => void;
  revertAction: (messageId: string, actionIndex: number) => void;

  // Context for AI
  currentChapterContent: string;
  currentSelection: string;
  selectionRange: { from: number; to: number } | null;
  selectionTimestamp: number | null;
  setChapterContent: (content: string) => void;
  setSelection: (selection: string, range?: { from: number; to: number }) => void;
  clearSelection: () => void;

  // Recent results for undo
  lastOriginalText: string | null;
  lastResultText: string | null;
  setLastResult: (original: string, result: string) => void;
  clearLastResult: () => void;
}

export const useAIStore = create<AIState>()(
  persist(
    (set, get) => ({
      // Quota
      quota: null,
      isLoadingQuota: false,

      fetchQuota: async () => {
        set({ isLoadingQuota: true });
        try {
          const quota = await api.getAIQuota();
          set({ quota, isLoadingQuota: false });
        } catch (error) {
          console.error('Failed to fetch AI quota:', error);
          set({ isLoadingQuota: false });
        }
      },

      // Processing state
      isProcessing: false,
      currentOperation: null,

      setProcessing: (operation) => {
        set({
          isProcessing: operation !== null,
          currentOperation: operation,
        });
      },

      // Chat state - per project
      currentProjectId: null,
      messagesByProject: {},
      messages: [], // Computed getter - see below
      isChatOpen: false,
      isRecording: false,

      setCurrentProject: (projectId) => {
        set((state) => ({
          currentProjectId: projectId,
          messages: projectId ? (state.messagesByProject[projectId] || []) : [],
        }));
      },

      toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
      openChat: () => set({ isChatOpen: true }),
      closeChat: () => set({ isChatOpen: false }),

      addMessage: (message) => {
        const newMessage: ChatMessage = {
          ...message,
          id: crypto.randomUUID(),
          timestamp: new Date(),
        };
        set((state) => {
          const projectId = state.currentProjectId;
          if (!projectId) return state;

          const projectMessages = state.messagesByProject[projectId] || [];
          const updatedMessages = [...projectMessages, newMessage];

          return {
            messagesByProject: {
              ...state.messagesByProject,
              [projectId]: updatedMessages,
            },
            messages: updatedMessages,
          };
        });
        return newMessage;
      },

      clearMessages: () => {
        set((state) => {
          const projectId = state.currentProjectId;
          if (!projectId) return state;

          return {
            messagesByProject: {
              ...state.messagesByProject,
              [projectId]: [],
            },
            messages: [],
          };
        });
      },

      clearProjectMessages: (projectId) => {
        set((state) => {
          const newMessagesByProject = { ...state.messagesByProject };
          delete newMessagesByProject[projectId];

          return {
            messagesByProject: newMessagesByProject,
            messages: state.currentProjectId === projectId ? [] : state.messages,
          };
        });
      },

      setRecording: (recording) => set({ isRecording: recording }),

      applyAction: (messageId, actionIndex) => {
        set((state) => {
          const projectId = state.currentProjectId;
          if (!projectId) return state;

          const projectMessages = state.messagesByProject[projectId] || [];
          const updatedMessages = projectMessages.map((m) => {
            if (m.id === messageId && m.actions && m.actions[actionIndex]) {
              const newActions = [...m.actions];
              newActions[actionIndex] = { ...newActions[actionIndex], applied: true };
              return { ...m, actions: newActions };
            }
            return m;
          });

          return {
            messagesByProject: {
              ...state.messagesByProject,
              [projectId]: updatedMessages,
            },
            messages: updatedMessages,
          };
        });
      },

      revertAction: (messageId, actionIndex) => {
        set((state) => {
          const projectId = state.currentProjectId;
          if (!projectId) return state;

          const projectMessages = state.messagesByProject[projectId] || [];
          const updatedMessages = projectMessages.map((m) => {
            if (m.id === messageId && m.actions && m.actions[actionIndex]) {
              const newActions = [...m.actions];
              newActions[actionIndex] = { ...newActions[actionIndex], applied: false };
              return { ...m, actions: newActions };
            }
            return m;
          });

          return {
            messagesByProject: {
              ...state.messagesByProject,
              [projectId]: updatedMessages,
            },
            messages: updatedMessages,
          };
        });
      },

      // Context
      currentChapterContent: '',
      currentSelection: '',
      selectionRange: null,
      selectionTimestamp: null,
      setChapterContent: (content) => set({ currentChapterContent: content }),
      setSelection: (selection, range) => set({
        currentSelection: selection,
        selectionRange: range || null,
        selectionTimestamp: selection ? Date.now() : null,
      }),
      clearSelection: () => set({
        currentSelection: '',
        selectionRange: null,
        selectionTimestamp: null,
      }),

      // Undo support
      lastOriginalText: null,
      lastResultText: null,

      setLastResult: (original, result) => {
        set({
          lastOriginalText: original,
          lastResultText: result,
        });
      },

      clearLastResult: () => {
        set({
          lastOriginalText: null,
          lastResultText: null,
        });
      },
    }),
    {
      name: 'ai-chat-storage',
      partialize: (state) => {
        // Keep last 30 messages per project
        const trimmedMessages: Record<string, ChatMessage[]> = {};
        for (const [projectId, messages] of Object.entries(state.messagesByProject)) {
          trimmedMessages[projectId] = messages.slice(-30);
        }
        return {
          messagesByProject: trimmedMessages,
          isChatOpen: state.isChatOpen,
        };
      },
    }
  )
);
